import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { 
  Camera, 
  Download, 
  Share2, 
  Trash2, 
  Copy, 
  ExternalLink,
  ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
  DownloadCloud,
  AlertTriangle,
  Eye,
  EyeOff,
  Lock,
  Upload,
  Calendar,
  Clock,
  Star,
  Users,
  Instagram,
  Check,
  Grid,
  List
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to calculate days remaining
const calculateDaysRemaining = (expirationDate) => {
  if (!expirationDate) return null;
  const expDate = new Date(expirationDate + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = expDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper function to format date in local format
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString();
};

// Check if device is mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

export default function AlbumPage() {
  const { code } = useParams();
  const { t } = useLanguage();
  const { isAuthenticated: isAdminAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showQR, setShowQR] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  
  // Tabs for massive events
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' | 'contributors'
  const [selectedContributor, setSelectedContributor] = useState(null);
  const [selectedContributors, setSelectedContributors] = useState([]);
  
  // Lightbox states for iOS-style carousel
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxOpacity, setLightboxOpacity] = useState(0);
  const [swipeOffsetY, setSwipeOffsetY] = useState(0);
  const [lockedDirection, setLockedDirection] = useState(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchCurrentX = useRef(0);
  const touchCurrentY = useRef(0);
  const isScrollingHorizontally = useRef(false);
  
  // Password protection states
  const [needsPassword, setNeedsPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const uploadUrl = `${window.location.origin}/upload/${code}`;

  // Block body scroll when lightbox is open
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [selectedPhoto]);

  const fetchAlbum = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/albums/${code}`);
      const albumData = response.data;
      setAlbum(albumData);
      
      // If admin is logged in, skip password check
      if (isAdminAuthenticated) {
        setIsAuthenticated(true);
        setNeedsPassword(false);
        return;
      }
      
      // Check if album is private
      if (albumData.is_private) {
        // Check if already authenticated in this session
        const sessionKey = `album_auth_${code}`;
        const clientKey = `client_access_${code}`;
        const isAuth = sessionStorage.getItem(sessionKey) === 'true' || sessionStorage.getItem(clientKey) === 'true';
        if (isAuth) {
          setIsAuthenticated(true);
        } else {
          setNeedsPassword(true);
        }
      } else {
        setIsAuthenticated(true);
      }
    } catch (error) {
      toast.error(t('albumNotFound'));
      console.error(error);
    }
  }, [code, t, isAdminAuthenticated]);

  const fetchPhotos = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      // Admin always sees ALL photos including hidden
      const response = await axios.get(`${API}/albums/${code}/photos?include_hidden=true`);
      setPhotos(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [code, isAuthenticated]);

  useEffect(() => {
    fetchAlbum();
  }, [fetchAlbum]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPhotos();
      const interval = setInterval(fetchPhotos, 5000);
      return () => clearInterval(interval);
    }
  }, [fetchPhotos, isAuthenticated]);

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      toast.error(t('enterAlbumPassword'));
      return;
    }
    
    setVerifying(true);
    try {
      await axios.post(`${API}/albums/${code}/verify-password`, { password });
      // Store authentication in session
      sessionStorage.setItem(`album_auth_${code}`, 'true');
      setIsAuthenticated(true);
      setNeedsPassword(false);
      setLoading(true); // Reset loading to fetch photos
      toast.success(t('accessGranted'));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('incorrectPassword'));
      } else {
        toast.error(t('failedToVerify'));
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(uploadUrl);
      toast.success(t('linkCopied'));
    } catch (error) {
      toast.error(t('failedToCopy'));
    }
  };

  // Detect if device is mobile for share API
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const handleDownloadPhoto = async (photo) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${photo.url}`);
      const blob = await response.blob();
      // Use the new filename (album_date_time format) for download
      const filename = photo.filename || photo.original_filename || `photo_${photo.id}.jpg`;
      
      // On mobile, try to use Web Share API for direct save to gallery
      if (isMobileDevice() && navigator.share && navigator.canShare) {
        const file = new File([blob], filename, { type: blob.type });
        const shareData = { files: [file] };
        
        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            toast.success(t('photoShared'));
            return;
          } catch (shareError) {
            // User cancelled or share failed, fall back to download
            if (shareError.name !== 'AbortError') {
              console.log('Share failed, falling back to download');
            }
          }
        }
      }
      
      // Fallback: standard download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('failedToDownload'));
    }
  };

  const handleDownloadAll = () => {
    window.location.href = `${API}/albums/${code}/download`;
    toast.success(t('preparingDownload'));
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/albums/${code}/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
      setPhotoToDelete(null);
      toast.success(t('photoDeleted'));
    } catch (error) {
      toast.error(t('failedToDelete'));
    }
  };

  const handleToggleVisibility = async (photoId, currentlyHidden) => {
    try {
      await axios.patch(`${API}/albums/${code}/photos/${photoId}/toggle-visibility`);
      
      // Update the photo's hidden state in place
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, hidden: !currentlyHidden } : p
      ));
      
      if (currentlyHidden) {
        toast.success(t('photoShown'));
      } else {
        toast.success(t('photoHiddenAdmin'));
      }
      setSelectedPhoto(null);
    } catch (error) {
      toast.error(t('failedToUpdate'));
    }
  };

  const handleToggleOfficial = async (photoId, currentlyOfficial) => {
    try {
      const response = await axios.patch(`${API}/albums/${code}/photos/${photoId}/toggle-official`);
      
      // Update the photo's official state in place
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, is_official: response.data.is_official } : p
      ));
      
      toast.success(response.data.is_official ? t('markAsOfficial') : t('removeOfficial'));
      setSelectedPhoto(null);
    } catch (error) {
      toast.error(t('failedToUpdate'));
    }
  };

  // ========== Contributors Functions (for Massive Events) ==========
  
  // Get list of contributors with photo counts
  const getContributors = () => {
    const contributorMap = {};
    photos.forEach(photo => {
      const instagram = photo.uploader_instagram || 'anónimo';
      if (!contributorMap[instagram]) {
        contributorMap[instagram] = { count: 0, photos: [] };
      }
      contributorMap[instagram].count++;
      contributorMap[instagram].photos.push(photo);
    });
    
    return Object.entries(contributorMap)
      .map(([instagram, data]) => ({
        instagram,
        count: data.count,
        photos: data.photos
      }))
      .sort((a, b) => b.count - a.count); // Sort by photo count descending
  };

  const contributors = album?.is_massive_event ? getContributors() : [];

  // Toggle contributor selection
  const toggleContributorSelection = (instagram) => {
    setSelectedContributors(prev => 
      prev.includes(instagram)
        ? prev.filter(i => i !== instagram)
        : [...prev, instagram]
    );
  };

  // Select all contributors
  const selectAllContributors = () => {
    const allInstagrams = contributors.map(c => c.instagram).filter(i => i !== 'anónimo');
    setSelectedContributors(allInstagrams);
  };

  // Deselect all contributors
  const deselectAllContributors = () => {
    setSelectedContributors([]);
  };

  // Copy selected contributors to clipboard
  const copySelectedContributors = () => {
    const text = selectedContributors.map(i => `@${i}`).join(' ');
    navigator.clipboard.writeText(text);
    toast.success(`${selectedContributors.length} ${t('contributorsCopied') || 'usuarios copiados'}`);
  };

  // Get filtered photos for contributor view or lightbox
  const getDisplayPhotos = () => {
    if (selectedContributor) {
      return photos.filter(p => (p.uploader_instagram || 'anónimo') === selectedContributor);
    }
    return photos;
  };

  const displayPhotos = getDisplayPhotos();

  // ========== iOS-style Lightbox Functions ==========
  
  // Open photo in lightbox with animation
  const openLightbox = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
    setSwipeOffsetY(0);
    setLightboxVisible(true);
    requestAnimationFrame(() => {
      setLightboxOpacity(1);
      setTimeout(() => {
        if (carouselRef.current) {
          const scrollPosition = index * window.innerWidth;
          carouselRef.current.scrollLeft = scrollPosition;
        }
      }, 50);
    });
  };

  // Close lightbox with animation
  const closeLightbox = () => {
    setLightboxOpacity(0);
    setTimeout(() => {
      setLightboxVisible(false);
      setSelectedPhoto(null);
      setSwipeOffsetY(0);
    }, 200);
  };

  // Handle scroll snap - detect which photo is visible
  const handleCarouselScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = window.innerWidth;
      const newIndex = Math.round(scrollLeft / itemWidth);
      const currentDisplayPhotos = getDisplayPhotos();
      if (newIndex !== selectedPhotoIndex && newIndex >= 0 && newIndex < currentDisplayPhotos.length) {
        setSelectedPhotoIndex(newIndex);
        setSelectedPhoto(currentDisplayPhotos[newIndex]);
      }
    }
  };

  // Navigate to specific photo
  const navigatePhoto = (direction) => {
    const newIndex = selectedPhotoIndex + direction;
    const currentDisplayPhotos = getDisplayPhotos();
    if (newIndex >= 0 && newIndex < currentDisplayPhotos.length && carouselRef.current) {
      const scrollPosition = newIndex * window.innerWidth;
      carouselRef.current.scrollLeft = scrollPosition;
    }
  };

  // Go to specific index
  const goToPhoto = (index) => {
    const currentDisplayPhotos = getDisplayPhotos();
    if (carouselRef.current && index >= 0 && index < currentDisplayPhotos.length) {
      const scrollPosition = index * window.innerWidth;
      carouselRef.current.scrollLeft = scrollPosition;
    }
  };

  // Directional locking touch handlers
  const handleLightboxTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchCurrentX.current = e.touches[0].clientX;
    touchCurrentY.current = e.touches[0].clientY;
    setLockedDirection(null);
    isScrollingHorizontally.current = false;
  };

  const handleLightboxTouchMove = (e) => {
    touchCurrentX.current = e.touches[0].clientX;
    touchCurrentY.current = e.touches[0].clientY;
    
    const diffX = touchCurrentX.current - touchStartX.current;
    const diffY = touchCurrentY.current - touchStartY.current;
    
    if (!lockedDirection && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        setLockedDirection('horizontal');
        isScrollingHorizontally.current = true;
      } else {
        setLockedDirection('vertical');
        isScrollingHorizontally.current = false;
        if (carouselRef.current) {
          carouselRef.current.style.overflowX = 'hidden';
        }
      }
    }
    
    if (lockedDirection === 'vertical' && diffY > 0) {
      e.preventDefault();
      setSwipeOffsetY(diffY);
      const newOpacity = Math.max(0.2, 1 - (diffY / 350));
      setLightboxOpacity(newOpacity);
    }
  };

  const handleLightboxTouchEnd = () => {
    const diffY = touchCurrentY.current - touchStartY.current;
    
    if (carouselRef.current) {
      carouselRef.current.style.overflowX = 'auto';
    }
    
    if (lockedDirection === 'vertical') {
      if (diffY > 100) {
        closeLightbox();
      } else {
        setSwipeOffsetY(0);
        setLightboxOpacity(1);
      }
    }
    
    setLockedDirection(null);
    isScrollingHorizontally.current = false;
  };

  const handleDownloadQR = async () => {
    const QR_SIZE = 1080;
    const LOGO_SIZE = 200; // Logo size in the center
    
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = QR_SIZE;
    canvas.height = QR_SIZE;
    
    // Draw white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, QR_SIZE, QR_SIZE);
    
    // Load and draw QR code
    const qrImg = new Image();
    qrImg.onload = async () => {
      // Draw QR scaled to 1080x1080
      ctx.drawImage(qrImg, 0, 0, QR_SIZE, QR_SIZE);
      
      // Load and draw logo in center
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = async () => {
        // Calculate center position
        const logoX = (QR_SIZE - LOGO_SIZE) / 2;
        const logoY = (QR_SIZE - LOGO_SIZE) / 2;
        
        // Draw white circle background for logo
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(QR_SIZE / 2, QR_SIZE / 2, LOGO_SIZE / 2 + 15, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw the logo
        ctx.drawImage(logo, logoX, logoY, LOGO_SIZE, LOGO_SIZE);
        
        // Convert canvas to blob for sharing/downloading
        const filename = `${album?.name || 'album'}-qr-code.png`;
        
        canvas.toBlob(async (blob) => {
          if (!blob) {
            toast.error(t('downloadFailed') || 'Error al generar QR');
            return;
          }
          
          // Try Web Share API first (works on mobile for saving to gallery)
          if (navigator.share && navigator.canShare) {
            try {
              const file = new File([blob], filename, { type: 'image/png' });
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                  files: [file],
                  title: `QR - ${album?.name || 'Album'}`,
                });
                toast.success(t('qrShared') || 'QR compartido');
                return;
              }
            } catch (err) {
              // User cancelled or share failed, fall back to download
              if (err.name !== 'AbortError') {
                console.log('Share failed, falling back to download');
              }
            }
          }
          
          // Fallback: traditional download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success(t('qrDownloaded') || 'QR descargado');
        }, 'image/png');
      };
      
      logo.onerror = async () => {
        // If logo fails to load, download QR without logo
        const filename = `${album?.name || 'album'}-qr-code.png`;
        
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          
          if (navigator.share && navigator.canShare) {
            try {
              const file = new File([blob], filename, { type: 'image/png' });
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file] });
                return;
              }
            } catch (err) {
              // Fall back to download
            }
          }
          
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 'image/png');
      };
      
      logo.src = '/logo-qr.png';
    };
    
    qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading && !needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('albumNotFound')}
        </h1>
        <Link to="/">
          <Button variant="outline">{t('goHome')}</Button>
        </Link>
      </div>
    );
  }

  // Password protection screen
  if (needsPassword && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col" data-testid="password-screen">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-4 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <Link 
              to="/" 
              className="flex items-center gap-1 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">{t('goHome')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="minimal" />
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-gradient-to-br from-indigo-600 to-pink-600 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  MFcreativemanager
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Password Form */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('protectedAlbum')}
              </h1>
              <p className="text-slate-600 mb-2">{album.name}</p>
              <p className="text-slate-500 text-sm mb-6">{t('enterPasswordToAccess')}</p>
              
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('enterAlbumPassword')}
                    className="h-12 rounded-xl pr-12"
                    data-testid="album-password-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleVerifyPassword();
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <Button
                  onClick={handleVerifyPassword}
                  disabled={verifying}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 text-white font-semibold"
                  data-testid="verify-password-btn"
                >
                  {verifying ? (
                    <div className="spinner" style={{ width: 20, height: 20 }} />
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('accessAlbum')}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="album-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/50 h-16 flex items-center px-4 md:px-8">
        <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{t('back')}</span>
            </Link>
            <div className="flex items-center gap-2">
              <img src="/mf-logo.png" alt="MFcreativemanager" className="w-8 h-8 object-contain" />
              <span className="font-bold text-lg text-slate-900 hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('appName')}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="minimal" />
            
            <Button
              variant="outline"
              size="sm"
              data-testid="copy-link-btn"
              onClick={handleCopyLink}
              className="rounded-full border-slate-200 hover:bg-slate-50"
            >
              <Copy className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('copyLink')}</span>
            </Button>

            {photos.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                data-testid="download-all-btn"
                onClick={handleDownloadAll}
                className="rounded-full border-slate-200 hover:bg-slate-50"
              >
                <DownloadCloud className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{t('downloadAll')}</span>
              </Button>
            )}
            
            <Dialog open={showQR} onOpenChange={setShowQR}>
              <DialogTrigger asChild>
                <Button
                  data-testid="show-qr-btn"
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-pink-600 text-white hover:opacity-90"
                >
                  <Share2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('qrCode')}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{t('shareQRCode')}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center p-6 space-y-6">
                  <div className="qr-container relative">
                    <QRCodeSVG
                      id="qr-code-svg"
                      value={uploadUrl}
                      size={200}
                      level="H"
                      includeMargin={true}
                      imageSettings={{
                        src: "/logo-qr.png",
                        x: undefined,
                        y: undefined,
                        height: 40,
                        width: 40,
                        excavate: true,
                      }}
                    />
                  </div>
                  <p className="text-center text-slate-600 text-sm">
                    {t('qrScanText')}
                  </p>
                  <div className="flex gap-3 w-full">
                    <Button
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={handleCopyLink}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {t('copyLink')}
                    </Button>
                    <Button
                      data-testid="download-qr-btn"
                      className="flex-1 rounded-full bg-gradient-to-r from-indigo-600 to-pink-600 text-white"
                      onClick={handleDownloadQR}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('downloadQR')}
                    </Button>
                  </div>
                  <a 
                    href={uploadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center gap-1"
                  >
                    {t('viewFullGallery')} <Upload className="w-3 h-3" />
                  </a>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Mobile View - Instagram Style */}
          {isMobile ? (
            <div className="flex flex-col items-center">
              {/* Cover Image with gradient overlay and title */}
              {album.cover_image_url ? (
                <div className="w-full mb-4 rounded-2xl overflow-hidden relative">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${album.cover_image_url}`}
                    alt={`${album.name} cover`}
                    className="w-full h-64 object-cover"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
                  {/* Title over gradient */}
                  <div className="absolute bottom-0 left-0 right-0 pb-4 text-center">
                    <h1 
                      className="text-2xl font-bold text-slate-900"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="album-title"
                    >
                      {album.name}
                    </h1>
                    <p className="mt-1 text-slate-500">
                      {album.photo_count || 0}{album.photo_limit ? `/${album.photo_limit}` : ''} {(album.photo_count || 0) !== 1 ? t('photosUploaded') : t('photoUploaded')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-full mb-4 h-32 rounded-2xl bg-gradient-to-b from-indigo-100 to-white flex flex-col items-center justify-end pb-4">
                  <h1 
                    className="text-2xl font-bold text-slate-900 text-center"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid="album-title"
                  >
                    {album.name}
                  </h1>
                  <p className="mt-1 text-slate-500 text-center">
                    {album.photo_count || 0}{album.photo_limit ? `/${album.photo_limit}` : ''} {(album.photo_count || 0) !== 1 ? t('photosUploaded') : t('photoUploaded')}
                  </p>
                </div>
              )}
              
              {/* Expiration Info - Mobile */}
              {album.event_date && album.expiration_date && (
                <div className="mt-4 w-full px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    <span className="text-indigo-700">{t('albumAvailable3Months')}</span>
                    <span className="text-indigo-300">•</span>
                    <span className="text-indigo-600">
                      {t('albumExpiresOn')} {formatDate(album.expiration_date)}
                    </span>
                  </div>
                  {(() => {
                    const days = calculateDaysRemaining(album.expiration_date);
                    if (days !== null && days > 0) {
                      return (
                        <div className="flex justify-center mt-2">
                          <span className="px-3 py-1 bg-indigo-200 text-indigo-800 rounded-full text-xs font-semibold">
                            {days} {days === 1 ? t('dayRemaining') : t('daysRemaining')}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          ) : (
            /* Desktop View - Original */
            <div className="mb-8">
              <h1 
                className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="album-title"
              >
                {album.name}
              </h1>
              <p className="mt-2 text-slate-600">
                {album.photo_count || 0}{album.photo_limit ? `/${album.photo_limit}` : ''} {(album.photo_count || 0) !== 1 ? t('photosUploaded') : t('photoUploaded')}
              </p>
              
              {/* Album Expiration Info */}
              {album.event_date && album.expiration_date && (
                <div className="mt-4 inline-flex items-center gap-3 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-xl">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  <div className="text-sm">
                    <span className="text-indigo-700">{t('albumAvailable3Months')}</span>
                    <span className="mx-2 text-indigo-300">•</span>
                    <span className="text-indigo-600">
                      {t('albumExpiresOn')} <strong>{formatDate(album.expiration_date)}</strong>
                    </span>
                    {(() => {
                      const days = calculateDaysRemaining(album.expiration_date);
                      if (days !== null && days > 0) {
                        return (
                          <span className="ml-2 px-2 py-0.5 bg-indigo-200 text-indigo-800 rounded-full text-xs font-semibold">
                            {days} {days === 1 ? t('dayRemaining') : t('daysRemaining')}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Album Almost Full Warning */}
          {album.photo_limit && (album.photo_limit - (album.photo_count || 0)) <= Math.ceil(album.photo_limit * 0.2) && (album.photo_limit - (album.photo_count || 0)) > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('albumAlmostFull')} ({album.photo_limit - (album.photo_count || 0)} {t('remainingPhotos')})
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {t('contactToUpgrade')}
                </p>
              </div>
            </div>
          )}

          {/* Album Full Warning */}
          {album.photo_limit && (album.photo_limit - (album.photo_count || 0)) <= 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('albumFull')}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {t('contactToUpgrade')}
                </p>
              </div>
            </div>
          )}

          {/* Tabs for Massive Events */}
          {album.is_massive_event && (
            <div className="mb-6">
              {/* Tab Buttons */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                <button
                  onClick={() => {
                    setActiveTab('photos');
                    setSelectedContributor(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium transition-all ${
                    activeTab === 'photos'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  {t('photosTab') || 'Fotos'}
                </button>
                <button
                  onClick={() => setActiveTab('contributors')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium transition-all ${
                    activeTab === 'contributors'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  {t('contributorsTab') || 'Participantes'}
                  <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
                    {contributors.length}
                  </span>
                </button>
              </div>

              {/* Contributor Filter Badge - when viewing photos from specific contributor */}
              {selectedContributor && activeTab === 'photos' && (
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-full">
                    <Instagram className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-700">
                      @{selectedContributor}
                    </span>
                    <span className="text-xs text-indigo-500">
                      ({displayPhotos.length} {displayPhotos.length === 1 ? 'foto' : 'fotos'})
                    </span>
                    <button
                      onClick={() => setSelectedContributor(null)}
                      className="ml-1 w-5 h-5 rounded-full bg-indigo-200 hover:bg-indigo-300 flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-indigo-700" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contributors Tab Content */}
          {album.is_massive_event && activeTab === 'contributors' && (
            <div className="space-y-4 overflow-x-hidden">
              {/* Selection Actions */}
              {contributors.filter(c => c.instagram !== 'anónimo').length > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button
                      onClick={selectedContributors.length === contributors.filter(c => c.instagram !== 'anónimo').length ? deselectAllContributors : selectAllContributors}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                        selectedContributors.length > 0 
                          ? 'bg-indigo-600 border-indigo-600 text-white' 
                          : 'border-slate-300 hover:border-indigo-400'
                      }`}
                    >
                      {selectedContributors.length > 0 && <Check className="w-4 h-4" />}
                    </button>
                    <span className="text-sm text-slate-600">
                      {selectedContributors.length > 0 
                        ? `${selectedContributors.length} ${t('selected') || 'seleccionados'}`
                        : t('selectAll') || 'Seleccionar todos'
                      }
                    </span>
                  </div>
                  {selectedContributors.length > 0 && (
                    <button
                      onClick={copySelectedContributors}
                      className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors text-sm w-full sm:w-auto justify-center"
                    >
                      <Copy className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{t('copyUsernames') || 'Copiar @'}</span>
                    </button>
                  )}
                </div>
              )}

              {/* Contributors List */}
              <div className="grid gap-2">
                {contributors.map((contributor) => (
                  <div
                    key={contributor.instagram}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 transition-colors overflow-hidden"
                  >
                    {/* Top row on mobile: checkbox, icon, username */}
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      {/* Selection Checkbox */}
                      {contributor.instagram !== 'anónimo' && (
                        <button
                          onClick={() => toggleContributorSelection(contributor.instagram)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                            selectedContributors.includes(contributor.instagram)
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'border-slate-300 hover:border-indigo-400'
                          }`}
                        >
                          {selectedContributors.includes(contributor.instagram) && <Check className="w-3 h-3" />}
                        </button>
                      )}
                      {contributor.instagram === 'anónimo' && <div className="w-5 flex-shrink-0" />}
                      
                      {/* Instagram Icon */}
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        contributor.instagram === 'anónimo' ? 'bg-slate-100' : 'bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500'
                      }`}>
                        {contributor.instagram === 'anónimo' 
                          ? <Users className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                          : <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        }
                      </div>
                      
                      {/* Username - Clickable to copy */}
                      <button
                        onClick={() => {
                          if (contributor.instagram !== 'anónimo') {
                            navigator.clipboard.writeText(`@${contributor.instagram}`);
                            toast.success(t('copiedToClipboard') || '¡Copiado!');
                          }
                        }}
                        className={`flex-1 text-left min-w-0 ${
                          contributor.instagram !== 'anónimo' 
                            ? 'hover:text-indigo-600 cursor-pointer' 
                            : 'cursor-default'
                        }`}
                      >
                        <p className="font-medium text-slate-900 truncate text-sm sm:text-base">
                          {contributor.instagram === 'anónimo' ? t('anonymous') || 'Anónimo' : `@${contributor.instagram}`}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-500">
                          {contributor.count} {contributor.count === 1 ? 'foto' : 'fotos'}
                        </p>
                      </button>
                    </div>
                    
                    {/* Bottom row on mobile: badge and button */}
                    <div className="flex items-center gap-2 pl-7 sm:pl-0">
                      {/* Photo Count Badge */}
                      <span className="px-2 sm:px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs sm:text-sm font-medium flex-shrink-0">
                        {contributor.count}
                      </span>
                      
                      {/* View Photos Button */}
                      <button
                        onClick={() => {
                          setSelectedContributor(contributor.instagram);
                          setActiveTab('photos');
                        }}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-medium transition-colors flex-shrink-0 text-xs sm:text-sm"
                      >
                        {t('viewPhotos') || 'Ver fotos'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {contributors.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">{t('noContributors') || 'Aún no hay participantes'}</p>
                </div>
              )}
            </div>
          )}

          {/* Photo Gallery - Show when photos tab is active or for non-massive events */}
          {(!album.is_massive_event || activeTab === 'photos') && (
            <>
          {displayPhotos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-6">
                <ImageIcon className="w-10 h-10 text-slate-400" />
              </div>
              <pre className="ascii-art mb-6">{`
    ╔═══════════════╗
    ║   📷   📸    ║
    ║  ${t('waitingForPhotos')}  ║
    ╚═══════════════╝
              `}</pre>
              <h2 
                className="text-xl font-bold text-slate-900 mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('noPhotosYet')}
              </h2>
              <p className="text-slate-600 max-w-md mb-6">
                {t('noPhotosDesc')}
              </p>
              <Button
                onClick={() => setShowQR(true)}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-pink-600 text-white"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t('shareQRCode')}
              </Button>
            </div>
          ) : isMobile ? (
            /* Mobile: Grid 3 columns */
            <div className="grid grid-cols-3 gap-1 mt-6" data-testid="photo-gallery">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`relative aspect-square rounded-lg overflow-hidden bg-slate-100 animate-fadeIn ${photo.hidden ? 'ring-2 ring-amber-300' : ''}`}
                  style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                  data-testid={`photo-${photo.id}`}
                  onClick={() => openLightbox(photo, index)}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                    alt={photo.original_filename}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${photo.hidden ? 'opacity-60' : ''}`}
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Hidden badge - only show if NOT massive event (massive uses star button position) */}
                  {photo.hidden && !album.is_massive_event && (
                    <div className="absolute top-1 left-1">
                      <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                        <EyeOff className="w-3 h-3 text-white" />
                      </div>
                    </div>
                  )}
                  {/* Official button - only for massive events */}
                  {album.is_massive_event && (
                    <button
                      className={`absolute top-1 left-1 w-6 h-6 rounded-full ${photo.is_official ? 'bg-indigo-500' : 'bg-black/50'} ${photo.hidden ? 'ring-2 ring-amber-400' : ''} text-white flex items-center justify-center z-10`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleOfficial(photo.id, photo.is_official);
                      }}
                      data-testid={`official-photo-${photo.id}`}
                    >
                      <Star className={`w-3 h-3 ${photo.is_official ? 'fill-white' : ''}`} />
                    </button>
                  )}
                  {/* Action buttons - always visible */}
                  <div className="absolute top-1 right-1 flex gap-0.5">
                    <button
                      className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPhoto(photo);
                      }}
                      data-testid={`download-photo-${photo.id}`}
                    >
                      <Download className="w-3 h-3" />
                    </button>
                    {/* Hide/show button - only for normal albums */}
                    {!album.is_massive_event && (
                      <button
                        className={`w-6 h-6 rounded-full ${photo.hidden ? 'bg-green-500' : 'bg-black/50'} text-white flex items-center justify-center`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVisibility(photo.id, photo.hidden);
                        }}
                        data-testid={`toggle-photo-${photo.id}`}
                      >
                        {photo.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      </button>
                    )}
                    <button
                      className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoToDelete(photo);
                      }}
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {/* Instagram username - only for massive events */}
                  {album.is_massive_event && photo.uploader_instagram && (
                    <div className="absolute bottom-1 left-1 right-1">
                      <button
                        className="text-[10px] text-white/70 font-medium bg-black/30 px-1.5 py-0.5 rounded backdrop-blur-sm hover:bg-black/50 hover:text-white transition-colors cursor-pointer select-text"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`@${photo.uploader_instagram}`);
                          toast.success(t('copiedToClipboard') || '¡Copiado!');
                        }}
                        title={t('clickToCopy') || 'Click para copiar'}
                      >
                        @{photo.uploader_instagram}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Desktop: Masonry Grid */
            <div className="masonry-grid" data-testid="photo-gallery">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`masonry-item photo-card rounded-xl overflow-hidden bg-slate-100 relative group cursor-pointer animate-fadeIn ${photo.hidden ? 'ring-2 ring-amber-300' : ''}`}
                  style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                  onClick={() => openLightbox(photo, index)}
                  data-testid={`photo-${photo.id}`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                    alt={photo.original_filename}
                    className={`w-full h-auto transition-opacity duration-300 ${photo.hidden ? 'opacity-60' : ''}`}
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Hidden badge - only show if NOT massive event */}
                  {photo.hidden && !album.is_massive_event && (
                    <div className="absolute top-2 left-2">
                      <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                        <EyeOff className="w-3 h-3 text-white" />
                      </div>
                    </div>
                  )}
                  {/* Official button - only for massive events */}
                  {album.is_massive_event && (
                    <button
                      className={`absolute top-2 left-2 w-8 h-8 rounded-full ${photo.is_official ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-black/50 hover:bg-indigo-500'} ${photo.hidden ? 'ring-2 ring-amber-400' : ''} text-white flex items-center justify-center transition-all z-10`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleOfficial(photo.id, photo.is_official);
                      }}
                      data-testid={`official-photo-${photo.id}`}
                      title={photo.is_official ? t('removeOfficial') : t('markAsOfficial')}
                    >
                      <Star className={`w-4 h-4 ${photo.is_official ? 'fill-white' : ''}`} />
                    </button>
                  )}
                  {/* Action buttons - always visible */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button
                      className="w-8 h-8 rounded-full bg-black/50 hover:bg-indigo-500 text-white flex items-center justify-center transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPhoto(photo);
                      }}
                      data-testid={`download-photo-${photo.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {/* Hide/show button - only for normal albums */}
                    {!album.is_massive_event && (
                      <button
                        className={`w-8 h-8 rounded-full ${photo.hidden ? 'bg-green-500 hover:bg-green-600' : 'bg-black/50 hover:bg-amber-500'} text-white flex items-center justify-center transition-all`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVisibility(photo.id, photo.hidden);
                        }}
                        data-testid={`toggle-photo-${photo.id}`}
                        title={photo.hidden ? t('showPhoto') : t('hidePhoto')}
                      >
                        {photo.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      className="w-8 h-8 rounded-full bg-black/50 hover:bg-red-500 text-white flex items-center justify-center transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoToDelete(photo);
                      }}
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Instagram username - only for massive events */}
                  {album.is_massive_event && photo.uploader_instagram && (
                    <div className="absolute bottom-2 left-2 right-2">
                      <button
                        className="text-xs text-white/60 font-medium bg-black/30 px-2 py-1 rounded backdrop-blur-sm hover:bg-black/50 hover:text-white transition-colors cursor-pointer select-text"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`@${photo.uploader_instagram}`);
                          toast.success(t('copiedToClipboard') || '¡Copiado!');
                        }}
                        title={t('clickToCopy') || 'Click para copiar'}
                      >
                        @{photo.uploader_instagram}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </div>
      </main>

      {/* Native iOS-style Scroll Snap Lightbox */}
      {lightboxVisible && selectedPhoto && (
        <div 
          className="fixed inset-0 z-50"
          style={{ 
            backgroundColor: `rgba(0, 0, 0, ${lightboxOpacity})`,
            transition: 'background-color 0.2s ease-out',
          }}
          onClick={closeLightbox}
        >
          {/* Close button - top right */}
          <button
            className="absolute top-4 right-4 z-40 w-11 h-11 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm"
            style={{ opacity: lightboxOpacity }}
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Photo counter - top left */}
          <div 
            className="absolute top-4 left-4 z-40 bg-black/40 px-4 py-2 rounded-full text-white text-sm font-medium backdrop-blur-sm"
            style={{ opacity: lightboxOpacity }}
          >
            {selectedPhotoIndex + 1} de {displayPhotos.length}
          </div>
          
          {/* Navigation arrows - desktop only */}
          {displayPhotos.length > 1 && selectedPhotoIndex > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white hidden md:flex items-center justify-center backdrop-blur-sm"
              style={{ opacity: lightboxOpacity }}
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto(-1);
              }}
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}
          {displayPhotos.length > 1 && selectedPhotoIndex < displayPhotos.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white hidden md:flex items-center justify-center backdrop-blur-sm"
              style={{ opacity: lightboxOpacity }}
              onClick={(e) => {
                e.stopPropagation();
                navigatePhoto(1);
              }}
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}
          
          {/* Native scroll snap carousel with directional locking */}
          <div 
            ref={carouselRef}
            className="absolute inset-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory"
            style={{ 
              scrollBehavior: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              zIndex: 10,
              touchAction: lockedDirection === 'vertical' ? 'none' : 'pan-x',
            }}
            onScroll={handleCarouselScroll}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleLightboxTouchStart}
            onTouchMove={handleLightboxTouchMove}
            onTouchEnd={handleLightboxTouchEnd}
          >
            <style>{`
              .hide-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>
            {displayPhotos.map((photo, index) => (
              <div 
                key={photo.id}
                className="flex-shrink-0 w-screen h-full flex items-center justify-center snap-center"
                style={{
                  transform: `translateY(${index === selectedPhotoIndex ? swipeOffsetY : 0}px) scale(${index === selectedPhotoIndex ? 1 - swipeOffsetY / 800 : 1})`,
                  transition: lockedDirection === null && swipeOffsetY === 0 ? 'transform 0.2s ease-out' : 'none',
                }}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                  alt={photo.original_filename}
                  className="max-w-[92%] max-h-[70vh] rounded-lg object-contain select-none"
                  style={{
                    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.4)',
                  }}
                  draggable={false}
                />
              </div>
            ))}
          </div>
          
          {/* Bottom controls */}
          <div 
            className="absolute bottom-0 left-0 right-0 z-40 pb-6 pt-4 pointer-events-none"
            style={{ 
              opacity: lightboxOpacity,
              background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
            }}
          >
            {/* Instagram username - only for massive events */}
            {album.is_massive_event && selectedPhoto.uploader_instagram && (
              <div className="flex justify-center mb-3 pointer-events-auto">
                <button
                  className="text-sm text-white/60 font-medium bg-black/20 px-3 py-1 rounded-full backdrop-blur-sm hover:bg-black/40 hover:text-white transition-colors cursor-pointer select-text"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(`@${selectedPhoto.uploader_instagram}`);
                    toast.success(t('copiedToClipboard') || '¡Copiado!');
                  }}
                  title={t('clickToCopy') || 'Click para copiar'}
                >
                  @{selectedPhoto.uploader_instagram}
                </button>
              </div>
            )}
            
            {/* Dot indicators */}
            {displayPhotos.length > 1 && displayPhotos.length <= 12 && (
              <div className="flex justify-center gap-1.5 mb-4 pointer-events-auto">
                {displayPhotos.map((_, idx) => (
                  <button
                    key={idx}
                    className={`h-2 rounded-full transition-all duration-200 ${
                      idx === selectedPhotoIndex 
                        ? 'bg-white w-5' 
                        : 'bg-white/40 w-2'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToPhoto(idx);
                    }}
                  />
                ))}
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex justify-center gap-2 px-4 pointer-events-auto flex-wrap">
              {/* Official button - only for massive events */}
              {album.is_massive_event && (
                <button
                  className={`px-4 py-2.5 rounded-full ${selectedPhoto.is_official ? 'bg-indigo-500' : 'bg-white/20'} text-white font-medium flex items-center gap-2 backdrop-blur-sm active:scale-95 transition-transform`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleOfficial(selectedPhoto.id, selectedPhoto.is_official);
                  }}
                >
                  <Star className={`w-4 h-4 ${selectedPhoto.is_official ? 'fill-white' : ''}`} />
                  {selectedPhoto.is_official ? t('removeOfficial') : t('markAsOfficial')}
                </button>
              )}
              <button
                className="px-5 py-2.5 rounded-full bg-white text-slate-900 font-medium flex items-center gap-2 shadow-lg active:scale-95 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadPhoto(selectedPhoto);
                }}
              >
                <Download className="w-4 h-4" />
                {t('download')}
              </button>
              {/* Hide/show button - only for normal albums */}
              {!album.is_massive_event && (
                <button
                  className={`px-4 py-2.5 rounded-full ${selectedPhoto.hidden ? 'bg-green-500' : 'bg-amber-500'} text-white font-medium flex items-center gap-2 active:scale-95 transition-transform`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleVisibility(selectedPhoto.id, selectedPhoto.hidden);
                  }}
                >
                  {selectedPhoto.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {selectedPhoto.hidden ? t('showPhoto') : t('hidePhoto')}
                </button>
              )}
              <button
                className="px-4 py-2.5 rounded-full bg-red-500 text-white font-medium flex items-center gap-2 active:scale-95 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoToDelete(selectedPhoto);
                  closeLightbox();
                }}
              >
                <Trash2 className="w-4 h-4" />
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Photo Confirmation Modal */}
      {photoToDelete && (
        <div 
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPhotoToDelete(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('deletePhotoConfirm')}
            </h3>
            <p className="text-slate-600 text-sm">
              {t('deletePhotoDesc')}
            </p>
            <div className="rounded-xl overflow-hidden bg-slate-100">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${photoToDelete.url}`}
                alt={photoToDelete.original_filename}
                className="w-full h-48 object-contain"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setPhotoToDelete(null)}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => handleDeletePhoto(photoToDelete.id)}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors"
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
